import { Link } from "react-router-dom";
import { FileText, ArrowLeft } from "@phosphor-icons/react";

export default function TermsOfServicePage() {
  const lastUpdated = "April 15, 2026";

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F9F9F7" }}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800 mb-6 transition-colors">
          <ArrowLeft size={16} /> Back to App
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <FileText size={28} weight="duotone" style={{ color: "#2F4F4F" }} />
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: "Work Sans", color: "#1C1917" }}>Terms of Service</h1>
        </div>
        <p className="text-sm text-stone-500 mb-8">Last updated: {lastUpdated}</p>

        <div className="bg-white rounded-lg border border-stone-200 p-6 sm:p-8 space-y-6 text-sm text-stone-700 leading-relaxed">
          <section>
            <h2 className="text-base font-semibold text-stone-800 mb-2" style={{ fontFamily: "Work Sans" }}>1. Acceptance of Terms</h2>
            <p>By accessing or using the Dining Floor Studio application (the "Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not use the Service.</p>
            <p className="mt-2">These Terms constitute a legally binding agreement between you and Dining Floor Studio ("we," "our," or "us").</p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-stone-800 mb-2" style={{ fontFamily: "Work Sans" }}>2. Description of Service</h2>
            <p>Dining Floor Studio is a restaurant floor management application that provides:</p>
            <ul className="list-disc pl-6 mt-1 space-y-1">
              <li>Floor plan creation and table management</li>
              <li>Reservation and walk-in tracking</li>
              <li>Server scheduling, section assignments, and performance tracking</li>
              <li>Waitlist management</li>
              <li>Member/guest CRM and loyalty tracking</li>
              <li>Real-time staff communication</li>
              <li>Service analytics and reporting</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-semibold text-stone-800 mb-2" style={{ fontFamily: "Work Sans" }}>3. User Accounts</h2>
            <p><strong>Registration:</strong> To use the Service, you must create an account with a valid email address and password. You are responsible for maintaining the confidentiality of your login credentials.</p>
            <p className="mt-2"><strong>Account Types:</strong> The Service supports three account roles:</p>
            <ul className="list-disc pl-6 mt-1 space-y-1">
              <li><strong>Admin</strong> — Full access to all features, settings, analytics, and user management</li>
              <li><strong>Host/Hostess</strong> — Access to floor plan, reservations, walk-ins, waitlist, and operational features</li>
              <li><strong>Server</strong> — Limited view of assigned section tables, reservations, and communication tools</li>
            </ul>
            <p className="mt-2"><strong>Account Restrictions:</strong> Each email address may only be registered once. Server accounts are permanently locked to the server role and cannot be changed to a different role. Only administrators may delete user accounts.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-stone-800 mb-2" style={{ fontFamily: "Work Sans" }}>4. Acceptable Use</h2>
            <p>You agree to use the Service only for its intended purpose of restaurant management. You agree <strong>not</strong> to:</p>
            <ul className="list-disc pl-6 mt-1 space-y-1">
              <li>Use the Service for any unlawful purpose</li>
              <li>Attempt to gain unauthorized access to other user accounts or system resources</li>
              <li>Share your login credentials with unauthorized individuals</li>
              <li>Interfere with or disrupt the operation of the Service</li>
              <li>Attempt to reverse-engineer, decompile, or exploit the Service for purposes other than its intended use</li>
              <li>Upload malicious code or content</li>
              <li>Use the Service to collect personal data of guests for purposes unrelated to restaurant operations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-semibold text-stone-800 mb-2" style={{ fontFamily: "Work Sans" }}>5. Data Ownership</h2>
            <p>All restaurant operational data entered into the Service (reservations, member profiles, floor plans, etc.) remains the property of the restaurant or business operating the Service.</p>
            <p className="mt-2">Dining Floor Studio does not claim ownership over any user-generated content or operational data.</p>
            <p className="mt-2">Administrators may export data at any time using the built-in CSV export functionality.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-stone-800 mb-2" style={{ fontFamily: "Work Sans" }}>6. Service Availability</h2>
            <p>We strive to maintain the availability of the Service but do not guarantee uninterrupted access. The Service may be temporarily unavailable due to:</p>
            <ul className="list-disc pl-6 mt-1 space-y-1">
              <li>Scheduled or unscheduled maintenance</li>
              <li>Server or infrastructure issues</li>
              <li>Network connectivity problems</li>
              <li>Force majeure events</li>
            </ul>
            <p className="mt-2">For self-hosted deployments, service availability is the responsibility of the hosting party.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-stone-800 mb-2" style={{ fontFamily: "Work Sans" }}>7. Notifications</h2>
            <p>The Service may send browser push notifications and in-app notifications related to restaurant operations. By enabling notifications, you consent to receiving these alerts. You may disable notifications at any time through your browser settings.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-stone-800 mb-2" style={{ fontFamily: "Work Sans" }}>8. Intellectual Property</h2>
            <p>The Dining Floor Studio name, logo, and application design are the intellectual property of Dining Floor Studio. Users are granted a limited, non-exclusive, non-transferable license to use the Service for its intended purpose.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-stone-800 mb-2" style={{ fontFamily: "Work Sans" }}>9. Limitation of Liability</h2>
            <p>To the fullest extent permitted by law, Dining Floor Studio shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to:</p>
            <ul className="list-disc pl-6 mt-1 space-y-1">
              <li>Loss of revenue or profits</li>
              <li>Loss of data</li>
              <li>Business interruption</li>
              <li>Any damages resulting from unauthorized access to or alteration of your data</li>
            </ul>
            <p className="mt-2">The Service is provided "as is" and "as available" without warranties of any kind, either express or implied.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-stone-800 mb-2" style={{ fontFamily: "Work Sans" }}>10. Indemnification</h2>
            <p>You agree to indemnify and hold harmless Dining Floor Studio from any claims, damages, losses, or expenses arising out of your use of the Service or violation of these Terms.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-stone-800 mb-2" style={{ fontFamily: "Work Sans" }}>11. Termination</h2>
            <p>We reserve the right to suspend or terminate your access to the Service at any time, with or without notice, for conduct that we believe violates these Terms or is harmful to other users or the Service.</p>
            <p className="mt-2">Administrators may delete user accounts at any time. Upon account deletion, associated data will be removed from the system.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-stone-800 mb-2" style={{ fontFamily: "Work Sans" }}>12. Governing Law</h2>
            <p>These Terms shall be governed by and construed in accordance with the laws of the State of Georgia, United States of America, without regard to its conflict of law provisions.</p>
            <p className="mt-2">Any disputes arising under these Terms shall be resolved in the courts of the State of Georgia.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-stone-800 mb-2" style={{ fontFamily: "Work Sans" }}>13. Changes to Terms</h2>
            <p>We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting. Your continued use of the Service after any modifications constitutes acceptance of the revised Terms.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-stone-800 mb-2" style={{ fontFamily: "Work Sans" }}>14. Contact Information</h2>
            <p>For questions about these Terms of Service, please contact:</p>
            <div className="mt-2 p-3 rounded-lg bg-stone-50 border border-stone-100">
              <p className="font-medium text-stone-800">Dining Floor Studio</p>
              <p>Email: <a href="mailto:jordanlthomas2002@gmail.com" className="underline" style={{ color: "#2F4F4F" }}>jordanlthomas2002@gmail.com</a></p>
              <p>Jurisdiction: State of Georgia, USA</p>
            </div>
          </section>

          <div className="pt-4 border-t border-stone-200 text-center text-stone-500 text-xs">
            <p>By using Dining Floor Studio, you acknowledge that you have read, understood, and agree to these Terms of Service and our <Link to="/privacy" className="underline" style={{ color: "#2F4F4F" }}>Privacy Policy</Link>.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
